import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  CardHeader,
  CardFooter,
  CardTitle,
  CardContent,
  DataTable,
  Skeleton,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@cargomatic/ui';
import Pagination from 'components/v2/PaginationV2';
import {
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
} from '@tanstack/react-table';
import Label from 'components/v2/forms/Label';
import Tooltip from 'components/ui/Tooltip';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { findUsers } from 'utils/UserWebAPIUtils';
import WebAPIUtils, {
  alreadyConfirmedLaneRatesV3,
  updateLaneRatesWithRateConditions,
  getLaneRatesWithRateConditions,
} from 'utils/AccessorialRequestWebAPIUtils';
import { showToast } from 'stores/ToastStore';
import { validateDocumentFilesSizes, isPDFEncrypted } from 'utils/documents';
import moment from 'moment-timezone';
import { FileUploader } from 'components/v2/FileUploader';
import { useRateConditionTypes } from 'hooks/useLaneRate';
import Alert from 'components/ui/Alert';
import _, { omitBy, isNil } from 'lodash';
import { CirclePlus, Trash, Eraser } from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';
import {
  createErrorMessage,
  laneRatesMapper,
  mapLaneRatesPayload,
} from './lane-rates-v2-helpers';
import LaneRateForm from './LaneRateForm';
import { NO_DATA } from 'utils/constants';

const AUDITS_PER_PAGE = 10;

function getAuditColumns(t) {
  return [
    {
      accessorKey: 'message',
      header: t('NAME'),
      cell: ({ getValue }) => (
        <div className="text-sm">{getValue() || NO_DATA}</div>
      ),
    },
    {
      accessorKey: 'createdAt',
      header: t('TIMESTAMP'),
      cell: ({ getValue }) => (
        <div className="text-sm whitespace-nowrap">
          {getValue() ? moment(getValue()).format('LLL') : NO_DATA}
        </div>
      ),
    },
    {
      accessorKey: 'user',
      header: t('SOURCE'),
      cell: ({ getValue }) => (
        <div className="text-sm">{getValue() || 'CSV Upload'}</div>
      ),
    },
  ];
}

function LaneRateAuditTab({ laneRateId }) {
  const { t } = useTranslation();
  const [audits, setAudits] = useState([]);
  const [auditsCount, setAuditsCount] = useState(0);
  const [auditsLoading, setAuditsLoading] = useState(false);
  const [auditPagination, setAuditPagination] = useState({
    pageIndex: 0,
    pageSize: AUDITS_PER_PAGE,
  });

  const auditColumns = useMemo(() => getAuditColumns(t), [t]);

  const fetchAudits = useCallback(async (id, pageIndex) => {
    setAuditsLoading(true);
    try {
      const skip = pageIndex * AUDITS_PER_PAGE;
      const params = new URLSearchParams({
        lanerateId: id,
        per_page: AUDITS_PER_PAGE,
        skip,
      });
      const response = await WebAPIUtils.getUserLaneRateAudits(
        params.toString(),
      );
      if (response) {
        setAudits(response.audits || []);
        setAuditsCount(response.count || 0);
      }
    } catch (error) {
      console.error('Failed to fetch lane rate audits:', error);
      setAudits([]);
      setAuditsCount(0);
    } finally {
      setAuditsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (laneRateId) {
      setAuditPagination((prev) => ({ ...prev, pageIndex: 0 }));
    }
  }, [laneRateId]);

  useEffect(() => {
    if (laneRateId) {
      fetchAudits(laneRateId, auditPagination.pageIndex);
    }
  }, [laneRateId, auditPagination.pageIndex, fetchAudits]);

  const auditTable = useReactTable({
    columns: auditColumns,
    data: audits,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    manualPagination: true,
    rowCount: auditsCount,
    state: {
      pagination: auditPagination,
    },
    onPaginationChange: setAuditPagination,
  });

  return (
    <CardContent className="space-y-4">
      <DataTable table={auditTable} isLoading={auditsLoading} />
      {auditsCount > AUDITS_PER_PAGE && (
        <Pagination
          page={auditPagination.pageIndex + 1}
          perPage={AUDITS_PER_PAGE}
          total={auditsCount}
          onPageClick={(page) =>
            setAuditPagination((prev) => ({
              ...prev,
              pageIndex: page - 1,
            }))
          }
        />
      )}
    </CardContent>
  );
}

const emptyLaneRate = {
  customerId: null,
  customerType: null,
  customerName: null,
  originZone: null,
  originZoneName: null,
  originState: null,
  originCity: null,
  originZip: null,
  destinationZone: null,
  destinationZoneName: null,
  destinationState: null,
  destinationCity: null,
  destinationZip: null,
  reversible: null,
  weeklyCommitment: null,
  rate: null,
  effectiveStart: null,
  effectiveEnd: null,
  eiaRegion: null,
  fscConfigId: null,
  alreadyConfirmed: false,
  overrideCustomerFsc: false,
  rateConditions: [],
  key: uuidv4(),
};

export default function LaneRatesEditor({
  className,
  laneRateId,
  isActionCreate,
}) {
  const { t } = useTranslation();
  const [saving, setSaving] = useState(false);
  const [saveDisabled, setSaveDisabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [savingFscConfigs, setSavingFscConfigs] = useState([]);
  const history = useHistory();
  const [files, setFiles] = useState([]);
  const [confirmed, setConfirmed] = useState(false);
  const [laneRates, setLaneRates] = useState([emptyLaneRate]);
  const location = useLocation();
  const [fileRequiredError, setFileRequiredError] = useState(false);
  const [fileEncryptedError, setFileEncryptedError] = useState(false);
  const [saveErrors, setSaveErrors] = useState([]);
  const [formValid, setFormValid] = useState(true);
  const { data } = useRateConditionTypes();
  const rateConditionTypes = data || [];
  const action = isActionCreate ? 'create' : 'update';

  useEffect(() => {
    if (!saving) {
      return;
    }

    const allSaved = savingFscConfigs.every((saving) => !saving);

    if (allSaved && saving) {
      continueSaving();
    }
  }, [savingFscConfigs]);

  useEffect(() => {
    async function fetchData() {
      const query = laneRateId ? `?id=${laneRateId}` : location.search;
      const result = await getLaneRatesWithRateConditions(query);
      const data = result.data;
      const { laneRates } = data;
      const mappedLaneRates = await laneRatesMapper(laneRates);
      setLaneRates(mappedLaneRates);
      setLoading(false);
    }
    if (action === 'update') {
      fetchData();
    } else {
      setLaneRates([emptyLaneRate]);
      setLoading(false);
    }
  }, [laneRateId]);

  useEffect(() => {
    validateFileUpload(files).then((valid) =>
      setSaveDisabled(!valid || !formValid),
    );
  }, [files]);

  const handleSave = async () => {
    setSaveErrors([]);
    if (saving) {
      return;
    }

    setSaving(true);
    setSavingFscConfigs(laneRates.map(() => true));

    // continueSaving() will be called after all fsc configs are saved
    // savingFscConfigs[] state triggers child forms to validate form then save FSC configs then call onSaveSuccess or onSaveFail
  };

  const continueSaving = async () => {
    if (!saving) {
      return;
    }

    if (confirmed) {
      try {
        validateDocumentFilesSizes(files);
      } catch (e) {
        console.error('Error validating document files sizes', e);
        return;
      }
    } else {
      laneRates.map((laneRate) => {
        delete laneRate.document;
        return {
          ...laneRate,
        };
      });
    }

    let laneRatesToSave = await addCustomerEmails(laneRates);

    // Change lane rate objects format to fit old API schema
    laneRatesToSave = laneRatesToSave.map((laneRate) => {
      const newLaneRate = {
        ...laneRate,
        alreadyConfirmed: confirmed,
        reversible: laneRate.reversible ? 'Yes' : 'No',
      };

      // Change date format to send like in old flow
      newLaneRate.effectiveStart = moment
        .utc(newLaneRate.effectiveStart)
        .format();
      newLaneRate.effectiveEnd = moment.utc(newLaneRate.effectiveEnd).format();

      return newLaneRate;
    });

    laneRatesToSave = mapLaneRatesPayload(laneRatesToSave, action);

    try {
      const response = confirmed
        ? await alreadyConfirmedLaneRatesV3(laneRatesToSave, files, action)
        : await updateLaneRatesWithRateConditions(laneRatesToSave, action);

      if (response?.entity?.success || response?.body?.success) {
        showToast(
          t('REQUEST_SUCCESS'),
          t('LANE_RATE_SUBMIT_SUCCESS'),
          'success',
        );
        goToLaneRates();
      } else {
        showToast(t('REQUEST_ERROR'), t('LANE_RATE_UPDATE_ERROR'), 'error');
        setSaving(false);
      }
    } catch (error) {
      setSaveErrors(error.body?.validationErrors);

      showToast(t('REQUEST_ERROR'), t('LANE_RATE_UPDATE_ERROR'), 'error');
      setSaving(false);
    }
  };

  const addCustomerEmails = async (laneRates) => {
    const customerIds = laneRates.map((laneRate) => laneRate.customerId);
    const findUsersResponse = await findUsers({ _id: { $in: customerIds } });
    const customers = findUsersResponse?.entity?.items;

    if (!customers) {
      return laneRates;
    }

    return laneRates.map((laneRate) => {
      const customer = customers.find(
        (customer) => customer.id === laneRate.customer,
      );
      return {
        ...laneRate,
        customerEmail: customer?.email,
      };
    });
  };

  const renderHeader = () => {
    const title = location.pathname.includes('add')
      ? t('ADD_LANE_RATE')
      : t('EDIT_LANE_RATES');

    return (
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>{title}</CardTitle>
          {isActionCreate && (
            <Button onClick={handleAddLaneRate} variant="outline" className="">
              Add Lane Rate
              <CirclePlus className="w-3.5 h-3.5 ml-2" />
            </Button>
          )}
        </div>
      </CardHeader>
    );
  };

  const uploadConfirmationDocuments = () => {
    if (confirmed) {
      return null;
    }

    return (
      <Button
        onClick={() => setConfirmed(true)}
        testDataId="lane-rate-save-and-upload-confirmation"
      >
        {t('SAVE_AND_UPLOAD_CONFIRMATION')}
      </Button>
    );
  };

  const onCancelClick = () => {
    setFileRequiredError(false);
    return confirmed ? setConfirmed(false) : goToLaneRates();
  };

  const renderFooter = () => {
    const actionLabel = t('SAVE_AND_COMPLETE_UPLOAD_CONFIRMATION');

    return (
      <CardFooter className="flex items-center justify-end space-x-4 bg-gray-50 pt-6">
        {uploadConfirmationDocuments()}
        {confirmed && (
          <Button
            variant="outline"
            onClick={handleSave}
            testDataId={`lane-rate-${actionLabel}`}
            disabled={saveDisabled}
          >
            {actionLabel}
          </Button>
        )}
        <Button
          color=""
          variant="outline"
          onClick={onCancelClick}
          testDataId="lane-rate-cancel-button"
        >
          {t('CANCEL')}
        </Button>
      </CardFooter>
    );
  };

  const goToLaneRates = () => {
    const listQueryFilterParameters = location.state?.listQueryFilterParameters;
    if (listQueryFilterParameters) {
      history.push('/lane-rates', { listQueryFilterParameters });
    } else {
      history.push('/lane-rates');
    }
  };

  const handleAddLaneRate = () => {
    const lastEdited = _.last(laneRates);
    const key = uuidv4();
    setLaneRates([...laneRates, { ...emptyLaneRate, ...lastEdited, key }]);
  };

  const handleClearLaneRate = (key) => {
    // Forcing render by changing the key
    setLaneRates((laneRates) => {
      const newLaneRates = laneRates.map((laneRate) => {
        if (laneRate.key === key) {
          return { ...emptyLaneRate, key: uuidv4() };
        }
        return laneRate;
      });
      return newLaneRates;
    });
  };

  const handleRemoveLaneRate = (i) => {
    setLaneRates([...laneRates.slice(0, i), ...laneRates.slice(i + 1)]);
  };

  const handleChangeLaneRate = (index, laneRate) => {
    const newLaneRates = [...laneRates];
    const newLaneRate = omitBy(laneRate, isNil);
    newLaneRates[index] = newLaneRate;
    setLaneRates(newLaneRates);
    setSaveErrors([]);
  };

  const validateFileUpload = async () => {
    if (confirmed && files.length === 0) {
      setFileRequiredError(true);
      return false;
    }

    const anyFilesEncryptedPDFs = (
      await Promise.all(files.map(isPDFEncrypted))
    ).some((isEncrypted) => isEncrypted);

    if (anyFilesEncryptedPDFs) {
      setFileEncryptedError(true);
      return false;
    }

    setFileRequiredError(false);
    setFileEncryptedError(false);

    return true;
  };

  const handleValidation = async (index, valid) => {
    const filesValid = await validateFileUpload();
    setFormValid(valid);
    if (!valid || !filesValid) {
      setSaving(false);
      setSavingFscConfigs([]);
      setSaveDisabled(true);
      return;
    }

    setSaveDisabled(false);

    if (!savingFscConfigs[index]) {
      return;
    }

    setSavingFscConfigs((prevState) => {
      const newSavingFscConfigs = [...prevState];
      newSavingFscConfigs[index] = false;
      return newSavingFscConfigs;
    });
  };

  const renderFileInput = () => {
    return (
      <div className="py-6">
        <Label className="mb-4">{t('UPLOAD_CONFIRMATION')}</Label>

        <FileUploader
          onChange={(files) => {
            setFiles(files);
            setFileRequiredError(false);
            setFileEncryptedError(false);
          }}
          fileTypesLabel={`${t('PDF')}, ${t('IMAGE')}`}
          multiple
        />

        {fileRequiredError && (
          <span
            data-testid="errorLabelFile"
            className="block text-xs italic text-red-500 mt-1"
          >
            {t('FILE_REQUIRED')}
          </span>
        )}
        {fileEncryptedError && (
          <span
            data-testid="errorLabelFile"
            className="block text-xs italic text-red-500 mt-1"
          >
            {t('PDF_DOCUMENT_ENCRYPTED')}
          </span>
        )}
      </div>
    );
  };

  const renderSaveErrors = (saveErrors) => {
    if (!saveErrors || !saveErrors.length) {
      return null;
    }
    return (
      <Alert
        type="error"
        title={`${t('PLEASE_ADDRESS_THE_FOLLOWING_ISSUES')}`}
        canClose
        onClose={() => setSaveErrors([])}
        className="mb-4"
      >
        <ul className="list-disc space-y-1 pl-5">
          {saveErrors.map((error, i) => (
            <li key={`save-errors-${i}`}>{createErrorMessage(error)}</li>
          ))}
        </ul>
      </Alert>
    );
  };

  if (loading) {
    return (
      <div className="w-full">
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-40" />
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              {Array.from({ length: 3 }).map((_, i) => (
                <div
                  key={i}
                  className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4"
                >
                  {Array.from({ length: 4 }).map((_, j) => (
                    <div key={j}>
                      <Skeleton className="h-3 w-20 mb-2" />
                      <Skeleton className="h-9 w-full" />
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="w-full">
      <Card className={className}>
        {renderHeader()}
        {renderSaveErrors(saveErrors)}
        {laneRates.map((laneRate, i) => (
          <div key={isActionCreate ? laneRate.key : laneRate.id}>
            <Tabs defaultValue="details" className="w-full">
              {!isActionCreate && (
                <div className="px-6 pb-3">
                  <TabsList className="justify-start">
                    <TabsTrigger value="details">
                      {t('LANE_RATE')} {i + 1}
                    </TabsTrigger>
                    <TabsTrigger value="audit">{t('AUDIT_TRAIL')}</TabsTrigger>
                  </TabsList>
                </div>
              )}

              <TabsContent value="details" className="mt-0">
                <CardContent className="py-4">
                  {isActionCreate && (
                    <div className="mb-4 flex items-center space-x-3">
                      <h3 className="">
                        {t('LANE_RATE')} {i + 1}
                      </h3>
                      <div className="flex">
                        <Tooltip
                          dataTestId={`Tooltip-rateconditionadd-${1}`}
                          id={1}
                          content={t('DELETE_LANE_RATE')}
                          tooltipClassName=""
                        >
                          <Button
                            variant="icon"
                            onClick={() => handleRemoveLaneRate(i)}
                            disabled={i === 0}
                          >
                            <Trash className="w-3.5 h-3.5" />
                          </Button>
                        </Tooltip>
                        <Tooltip
                          dataTestId={`Tooltip-rateconditionremove-${2}`}
                          id={2}
                          content={t('CLEAR_LANE_RATE_FIELDS')}
                          tooltipClassName=""
                        >
                          <Button
                            variant="icon"
                            onClick={() => handleClearLaneRate(laneRate.key)}
                            className="px-0"
                          >
                            <Eraser className="w-3.5 h-3.5" />
                          </Button>
                        </Tooltip>
                      </div>
                    </div>
                  )}
                  <LaneRateForm
                    isActionCreate={isActionCreate}
                    initialLaneRates={null}
                    laneRate={laneRate}
                    onChange={(lr) => handleChangeLaneRate(i, lr)}
                    saving={saving}
                    onValidate={(valid) => handleValidation(i, valid)}
                    rateConditionTypes={rateConditionTypes}
                  />
                </CardContent>
              </TabsContent>

              {!isActionCreate && (
                <TabsContent value="audit" className="mt-0">
                  <LaneRateAuditTab laneRateId={laneRate.id} />
                </TabsContent>
              )}
            </Tabs>
            {i < laneRates.length - 1 && (
              <hr className="mx-6 border-gray-200" />
            )}
          </div>
        ))}

        {confirmed && <CardContent>{renderFileInput()}</CardContent>}
        {renderFooter()}
      </Card>
    </div>
  );
}
